import { emailRegexCheck } from '@adornis/users/db/validators.js';

/**
 * Validates that `raw` is exactly ONE well-formed email address.
 *
 * `emailRegexCheck` already rejects most malformed input, but we additionally
 * reject any whitespace / list separators up front so multi-address strings like
 * "info@example.de; second@example.de" are caught with a clear message instead of
 * being stored verbatim as a single User.email (see the brueggen-engineering case).
 *
 * Isomorphic: only depends on the shared regex, safe to import on client and server.
 */
export const isValidSingleEmail = (raw: string | null | undefined): boolean => {
  const email = (raw ?? '').trim();
  if (!email) return false;
  if (/[\s,;]/.test(email)) return false; // blocks "a@x.de; b@x.de", "a@x.de, b@x.de", etc.
  return emailRegexCheck(email.toLowerCase());
};

/**
 * Returns the normalized (trimmed + lowercased) address, or throws a user-facing
 * error if it is empty, malformed, or actually contains multiple addresses.
 * Use at every point where a free-text email enters the system.
 */
export const normalizeSingleEmail = (raw: string | null | undefined): string => {
  const email = (raw ?? '').trim().toLowerCase();
  if (!email) throw new Error('Bitte geben Sie eine E-Mail-Adresse an.');
  if (!isValidSingleEmail(email))
    throw new Error('Bitte geben Sie genau eine gültige E-Mail-Adresse an.');
  return email;
};
